import { ReactElement, useState } from 'react';
import InputCheckbox from '~/components/input/InputCheckbox';
import Sections from '~/components/layout/Sections';
import PerformableGroupForm, {
  PerformableGroupNested,
  usePerformableGroup,
} from '~/components/performable/PerformableGroupForm';
import ZodFieldHidden from '~/components/zod/ZodFieldHidden';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import BrePackageGroup from '~/tenants/bre/package/BrePackageGroup';
import {
  BrePackageBoostInclude,
  BrePackageDuskInclude,
  BrePackageFloorplanInclude,
  BrePackageMatterportUpgrade,
  BrePackagePhotosInclude,
  BrePackageSocialInclude,
  BrePackageWebsiteInclude,
} from '~/tenants/bre/package/common';
import BreListingDisclosure from '~/tenants/bre/performable/BreListingDisclosure';
import BreBoostConfig from '~/tenants/bre/performable/boost/BreBoostConfig';
import { BreBoostFields } from '~/tenants/bre/performable/boost/BreBoostFields';
import BreDuskConfig from '~/tenants/bre/performable/dusk/BreDuskConfig';
import BreFloorplanAddOns from '~/tenants/bre/performable/floorplan/BreFloorplanAddOns';
import BreFloorplanConfig from '~/tenants/bre/performable/floorplan/BreFloorplanConfig';
import BreMatterportAddOns from '~/tenants/bre/performable/matterport/BreMatterportAddOns';
import BreMatterportConfig from '~/tenants/bre/performable/matterport/BreMatterportConfig';
import BrePhotosCommunityAddOn from '~/tenants/bre/performable/photos/BrePhotosCommunityAddOn';
import BrePhotosConfig from '~/tenants/bre/performable/photos/BrePhotosConfig';
import BrePhotosFields from '~/tenants/bre/performable/photos/BrePhotosFields';
import BrePhotosLocalAreaAddOn from '~/tenants/bre/performable/photos/BrePhotosLocalAreaAddOn';
import BreSocialAddOns from '~/tenants/bre/performable/social/BreSocialAddOns';
import BreSocialConfig from '~/tenants/bre/performable/social/BreSocialConfig';
import BreWebsiteConfig from '~/tenants/bre/performable/website/BreWebsiteConfig';
import BreWebsiteDomianAddOn from '~/tenants/bre/performable/website/BreWebsiteDomianAddOn';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { TenantPackageIncludes } from '~/tenants/common/package';

export default function BreSocialPackageForm(): ReactElement {
  const { order, hasPerformable } = usePerformableGroup<BreOrderContext>();

  return (
    <Sections>
      <TenantPackageIncludes>
        <PerformableGroupNested performable={BrePhotosConfig}>
          <BrePackagePhotosInclude itemNum={1} />
        </PerformableGroupNested>
        <BrePackageFloorplanInclude itemNum={2} />
        <BrePackageSocialInclude itemNum={3} />
        <PerformableGroupNested performable={BreBoostConfig}>
          <BrePackageBoostInclude itemNum={4} />
        </PerformableGroupNested>
        <BrePackageWebsiteInclude itemNum={5} />
        <BrePackageDuskInclude itemNum={6} />
      </TenantPackageIncludes>

      <BrePackageGroup itemNum={1} subtitle="Customize the ground photography." performable={BrePhotosConfig}>
        <BrePhotosFields context={order} />
        <BrePhotosCommunityAddOn context={order} persona={PersonaType.CUSTOMER} />
        <BrePhotosLocalAreaAddOn context={order} persona={PersonaType.CUSTOMER} />
      </BrePackageGroup>

      <BrePackageGroup itemNum={2} subtitle="Earth-Tone Color 2D Floorplan." performable={BreFloorplanConfig}>
        <BrePackageMatterportUpgrade />
        <BreFloorplanAddOns context={order} persona={PersonaType.CUSTOMER} />
      </BrePackageGroup>

      {hasPerformable(BreMatterportConfig) && (
        <PerformableGroupForm performable={BreMatterportConfig}>
          <BreMatterportAddOns context={order} persona={PersonaType.CUSTOMER} />
        </PerformableGroupForm>
      )}

      <BrePackageGroup itemNum={3} subtitle="Engages buyers and attracts future clients." performable={BreSocialConfig}>
        <BreSocialAddOns persona={PersonaType.CUSTOMER} />
      </BrePackageGroup>

      <BrePackageGroup
        itemNum={4}
        subtitle="Market your listing in the city of the property."
        performable={BreBoostConfig}
      >
        <BreBoostFields />
      </BrePackageGroup>

      <BrePackageGroup
        itemNum={5}
        subtitle="1-year subscription to a single property website page"
        performable={BreWebsiteConfig}
      >
        <ZodFieldHidden name="type" value="website" />
        <BreWebsiteDomianAddOn type={order.metadata.type} persona={PersonaType.CUSTOMER} />
      </BrePackageGroup>

      <PerformableGroupForm performable={BreDuskConfig}>
        <ZodFieldHidden name="quantity" value="1" />
      </PerformableGroupForm>
    </Sections>
  );
}
